import React, { useEffect } from "react";
import { US, ES, IT, BR } from "country-flag-icons/react/3x2";

import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";

import { useTranslation } from "react-i18next";
import { Divider } from "@material-ui/core";
import i18next from "i18next";
import { isFirefox } from "react-device-detect";
import DownloadUverifyExtensionModal from "components/Navbars/DownloadUverifyExtensionModal";
import {
  useDrawerOpen,
  useLanguageSettings,
} from "store/user-settings/user-settings-store";
import {
  ApplicationLanguage,
  Language,
  langueType,
  TimeZoneLanguage,
} from "i18n/ApplicationLanguagesEnum";
import { useSyncFieldglass } from "services/input-core/sync/fieldglass-sync";
import { checkMessageErrors } from "utils/functions";
import { toast } from "react-toastify";
import { useSubscription } from "store/auth/auth-store";
import { useKeycloak } from "@react-keycloak/web";
import { Close } from "@mui/icons-material";
import { useClientConfig } from "hooks/useClientConfig";

const DrawerContent = styled(Box)`
  padding: 20px;
  width: 400px;
`;

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
`;

const SubTitle = styled(Typography)`
  font-size: 1.2rem;
  margin-bottom: 10px;
`;

const Description = styled(Typography)`
  font-size: 0.9rem;
  opacity: 0.65;
`;

interface CustomDrawerConfigsProps {
  clientConfig: { browserExtensionConfig: { chromium: string } };
  isClientConfigLoading: boolean;
}

export const CustomDrawerConfigs: React.FC<CustomDrawerConfigsProps> = ({
  clientConfig,
  isClientConfigLoading,
}) => {
  const { t } = useTranslation("side-drawer");
  const { isKuo } = useSubscription();
  const { keycloak } = useKeycloak();
  const { language, setLanguage } = useLanguageSettings();
  const { clientConfigSidebar } = useClientConfig();

  const { drawerOpen, setDrawerOpen } = useDrawerOpen();

  const {
    mutate: syncEntity,
    isLoading: isSyncingEntity,
  } = useSyncFieldglass();

  const [
    openDownloadExtensionModal,
    setOpenDownloadExtensionModal,
  ] = React.useState(false);

  const [currentLanguage, setCurrentLanguage] = React.useState<string>(() => {
    const language = clientConfigSidebar?.timeZone ?? "Europe/London";
    i18next.changeLanguage(TimeZoneLanguage[language]);
    setLanguage(TimeZoneLanguage[language]);

    return TimeZoneLanguage[language];
  });

  useEffect(() => {
    const langue = i18next?.language as langueType;
    setCurrentLanguage(ApplicationLanguage[Language[langue]] ?? language);
  }, [clientConfigSidebar]);

  const handleDownloadUverifyExtension = () =>
    setOpenDownloadExtensionModal(true);

  const onChangeLanguage = (language: string) => {
    setLanguage(language);
    setCurrentLanguage(language);
    i18next.changeLanguage(language);
  };

  const handleOpenLinkInNewTab = () => {
    let linkUrl = "";

    switch (currentLanguage) {
      case "es":
        linkUrl =
          "https://uverifystorage.blob.core.windows.net/static/manual_del_proveedor_Grupo_KUO_es.pdf";
        break;
      case "en":
      default:
        linkUrl =
          "https://uverifystorage.blob.core.windows.net/static/manual_del_proveedor_Grupo_KUO_en.pdf";
        break;
    }

    window.open(linkUrl, "_blank")?.focus();
  };

  const handleSyncButtons = (entity: string) => {
    syncEntity(entity, {
      onSuccess: () => {
        toast.success(t("success-sync", { ns: "list-messages" }));
      },
      onError: (error: any) => {
        toast.error(
          t(
            checkMessageErrors(
              error?.response?.data?.error,
              "ERR_VALIDATE_DEFAULT",
            ),
            { ns: "list-errors" },
          ),
        );
      },
    });
  };

  const canSeeTheSynchronization = () => keycloak.hasRealmRole("Sync Manager");

  return (
    <>
      {openDownloadExtensionModal && (
        <DownloadUverifyExtensionModal
          open={openDownloadExtensionModal}
          onClose={() => {
            setOpenDownloadExtensionModal(false);
          }}
        />
      )}

      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        // onClose={() => setDrawerOpen(false)}
        transitionDuration={{
          enter: 300,
          exit: 300,
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <Title variant="h1" gutterBottom>
              {t("drawer-title")}
            </Title>
            <Close
              style={{
                cursor: "pointer",
                fontSize: "1.5rem",
              }}
              onClick={() => {
                setDrawerOpen(false);
              }}
            />
          </DrawerHeader>

          <Divider style={{ backgroundColor: "#808080" }} />

          <List>
            <ListItem>
              <ListItemText>
                <SubTitle variant="h6" gutterBottom>
                  {t("change-language")}
                </SubTitle>
                <Description variant="body2">
                  {t("change-language-guideline")}
                </Description>
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                <Typography id="language-select-label">
                  {t("select-language")}
                </Typography>
                <Select
                  labelId="language-select-label"
                  id="language-select"
                  value={currentLanguage}
                  onChange={(e) => onChangeLanguage(e.target.value)}
                >
                  <MenuItem value="en">
                    <US
                      title={t("english")}
                      style={{ height: 20, marginRight: 10 }}
                    />
                    {t("english")}
                  </MenuItem>
                  <MenuItem value="es">
                    <ES
                      title={t("spanish")}
                      style={{ height: 20, marginRight: 10 }}
                    />
                    {t("spanish")}
                  </MenuItem>
                  <MenuItem value="it">
                    <IT
                      title={t("italian")}
                      style={{ height: 20, marginRight: 10 }}
                    />
                    {t("italian")}
                  </MenuItem>
                  <MenuItem value="pt">
                    <BR
                      title={t("portuguese")}
                      style={{ height: 20, marginRight: 10 }}
                    />
                    {t("portuguese")}
                  </MenuItem>
                </Select>
              </ListItemText>
            </ListItem>
          </List>
          <Divider style={{ backgroundColor: "#808080" }} />

          {canSeeTheSynchronization() && (
            <List>
              <ListItem>
                <ListItemText>
                  <SubTitle variant="h6" gutterBottom>
                    {t("sync-entities-title")}
                  </SubTitle>
                  <Description variant="body2">
                    {t("sync-entities-guideline")}
                  </Description>
                </ListItemText>
              </ListItem>
              {isSyncingEntity ? (
                <ListItem>
                  <ListItemText>
                    <Description
                      variant="h3"
                      style={{ marginBottom: 20, fontSize: 20 }}
                    >
                      {t("sync-entities-syncing")}
                    </Description>
                    <CircularProgress color="warning" />
                  </ListItemText>
                </ListItem>
              ) : (
                <>
                  <ListItem>
                    <ListItemText>
                      <Button
                        disabled={isSyncingEntity}
                        variant="outlined"
                        color="warning"
                        onClick={() => handleSyncButtons("supplier")}
                      >
                        {t("suppliers")}
                      </Button>
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      <Button
                        disabled={isSyncingEntity}
                        variant="outlined"
                        color="warning"
                        onClick={() => handleSyncButtons("sow")}
                      >
                        {t("sows")}
                      </Button>
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      <Button
                        disabled={isSyncingEntity}
                        variant="outlined"
                        color="warning"
                        onClick={() => handleSyncButtons("worker")}
                      >
                        {t("workers")}
                      </Button>
                    </ListItemText>
                  </ListItem>

                  <ListItem>
                    <ListItemText>
                      <Button
                        disabled={isSyncingEntity}
                        variant="outlined"
                        color="warning"
                        onClick={() => handleSyncButtons("workerRole")}
                      >
                        {t("worker-role")}
                      </Button>
                    </ListItemText>
                  </ListItem>
                </>
              )}
            </List>
          )}

          <Divider style={{ backgroundColor: "#808080" }} />

          <List>
            <ListItem>
              <ListItemText>
                <SubTitle variant="h6" gutterBottom>
                  {t("download-uv-extension-title")}
                </SubTitle>
                <Description variant="body2">
                  {t("download-uv-extension-guideline")}
                </Description>
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={isClientConfigLoading}
                  onClick={
                    !isFirefox
                      ? () =>
                          window
                            .open(
                              clientConfig?.browserExtensionConfig?.chromium,
                              "_blank",
                            )
                            .focus()
                      : handleDownloadUverifyExtension
                  }
                >
                  {t("download-uv-extension-title")}
                </Button>
              </ListItemText>
            </ListItem>
          </List>

          <Divider style={{ backgroundColor: "#808080" }} />

          {isKuo && (
            <List>
              <ListItem>
                <ListItemText>
                  <SubTitle variant="h6" gutterBottom>
                    {t("open-user-manual-in-new-tab")}
                  </SubTitle>
                  <Description variant="body2">
                    {t("open-user-manual-in-new-tab-guideline")}
                  </Description>
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleOpenLinkInNewTab}
                  >
                    {t("open-user-manual-button-text")}
                  </Button>
                </ListItemText>
              </ListItem>
            </List>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};
